/*
 * Zorevella wishlist heart colour and header count
 *
 * Written against the app's real markup rather than assumptions about it.
 *
 * 1. The app already handles its own counter correctly. It writes the number
 *    into span.wishlist-hero-items-count and sets that span to display:none
 *    when the list empties. The number appeared stuck at 1 only because a rule
 *    in ai-globals forces that span to display:inline-block with !important,
 *    which beats the app's inline style, so the app's "hide it now" was being
 *    ignored. Nothing needs correcting; the hiding just needs to be let
 *    through, which is what the zv-count-hidden class below does.
 *
 * 2. Saved state must not be inferred from colour. An earlier version painted
 *    anything with a resolved fill, and the empty heart's <svg> has no fill
 *    attribute, so it inherits the initial black and counted as filled. Every
 *    heart went red whether saved or not. State now comes from the app itself,
 *    via the button's own aria-label, with its localStorage list as a second
 *    opinion.
 *
 * 3. The app updates that aria-label a beat after the heart is redrawn, so
 *    reading state on the click showed the old value and the heart sat grey for
 *    half a second before turning red. The click is therefore trusted
 *    immediately and held as pending for a couple of seconds, during which it
 *    outranks what the label says. Reality takes over once the app has caught
 *    up, so a failed save still corrects itself.
 */
(function () {
  var RED = '#c8102e';
  var BUTTON = '.wishlist-hero-custom-button';
  var COUNT = '.wishlist-hero-items-count';
  var CARD = '.wishlist-hero-list-card';
  var HOLD = 2500;

  var observer = null;
  var queued = false;
  var started = false;
  var pending = {};

  function injectStyles() {
    var style = document.createElement('style');
    style.setAttribute('data-zv-heart', '');
    style.textContent = [
      // Saved heart. Scoped to shapes the app actually paints, so an outline
      // that declares fill="none" is not accidentally filled in.
      BUTTON + '.zv-saved svg,',
      BUTTON + '.zv-saved svg *:not([fill="none"]),',
      CARD + ' svg,',
      CARD + ' svg *:not([fill="none"])',
      '{ fill: ' + RED + ' !important; }',
      BUTTON + '.zv-saved svg [stroke]:not([stroke="none"]),',
      CARD + ' svg [stroke]:not([stroke="none"])',
      '{ stroke: ' + RED + ' !important; }',
      // Let the app hide its own counter when the list empties.
      COUNT + '.zv-count-hidden { display: none !important; }',
    ].join('\n');
    document.head.appendChild(style);
  }

  // The app mirrors the guest list into localStorage under a key ending
  // wl-itms. The shape of each entry is not documented, so membership is
  // tested by looking for the product id anywhere inside the entry.
  function savedEntries() {
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var key = window.localStorage.key(i);
        if (key.indexOf('wl-itms') === -1) continue;
        var parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
        if (Array.isArray(parsed)) {
          return parsed.map(function (entry) {
            return JSON.stringify(entry);
          });
        }
      }
    } catch (error) {
      return null;
    }
    return null;
  }

  // The app labels its own button: "Add to wishlist" when the product is not
  // saved, and the removal wording once it is. That label is the app telling us
  // the state directly, so it is trusted ahead of anything inferred.
  function readState(container, entries) {
    var button = container.querySelector('button');
    var label = '';
    if (button) {
      label = button.getAttribute('aria-label') || button.getAttribute('title') || '';
    }

    if (/remove|saved|in wishlist/i.test(label)) return true;
    if (/add/i.test(label)) return false;

    var id = container.getAttribute('data-wlh-id');
    if (entries && id) {
      return entries.some(function (entry) {
        return entry.indexOf(id) !== -1;
      });
    }

    return false;
  }

  // A click is the shopper's intent, and it is correct well before the app
  // gets round to saying so. It wins until it expires.
  function currentState(container, entries) {
    var id = container.getAttribute('data-wlh-id');
    var held = id ? pending[id] : null;

    if (held) {
      if (Date.now() < held.until) return held.saved;
      delete pending[id];
    }

    return readState(container, entries);
  }

  function run() {
    queued = false;
    if (observer) observer.disconnect();

    var entries = savedEntries();

    document.querySelectorAll(BUTTON).forEach(function (container) {
      container.classList.toggle('zv-saved', currentState(container, entries));
    });

    document.querySelectorAll(COUNT).forEach(function (el) {
      el.classList.toggle('zv-count-hidden', el.style.display === 'none');
    });

    if (observer) {
      observer.takeRecords();
      observe();
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(run);
  }

  function observe() {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-label', 'title', 'data-wlh-id'],
    });
  }

  function watchClicks() {
    document.addEventListener(
      'click',
      function (event) {
        if (!event.target || !event.target.closest) return;

        var container = event.target.closest(BUTTON);
        if (!container) {
          // Removing from the wishlist page still needs a re-read, but nothing
          // there is optimistic: those cards leave the page entirely.
          if (event.target.closest(CARD)) {
            [150, 500, 1200].forEach(function (delay) {
              window.setTimeout(schedule, delay);
            });
          }
          return;
        }

        var next = !currentState(container, savedEntries());
        var id = container.getAttribute('data-wlh-id');
        if (id) pending[id] = { saved: next, until: Date.now() + HOLD };

        // Paint this one now rather than waiting for the next frame, so there
        // is no grey step at all.
        container.classList.toggle('zv-saved', next);

        [150, 500, 1200, HOLD + 100].forEach(function (delay) {
          window.setTimeout(schedule, delay);
        });
      },
      true
    );
  }

  /* Diagnostic panel, only on ?zvdebug=1. Left in place because it is what
     made this fix possible; it costs nothing when the flag is absent. */
  function debugPanel() {
    if (window.location.search.indexOf('zvdebug=1') === -1) return;

    var box = document.createElement('div');
    box.setAttribute('style', [
      'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:99999',
      'max-height:55vh', 'overflow:auto', 'background:#111', 'color:#0f0',
      'font:11px/1.45 ui-monospace,Menlo,Consolas,monospace',
      'padding:12px 14px', 'white-space:pre-wrap', 'word-break:break-all',
    ].join(';'));

    function render() {
      var count = document.querySelector(COUNT);
      var first = document.querySelector(BUTTON);
      var entries = savedEntries();

      box.textContent = [
        '--- ZOREVELLA WISHLIST DEBUG ---',
        '',
        'COUNT SPAN: ' + (count ? JSON.stringify(count.textContent) : 'NOT FOUND'),
        'COUNT DISPLAY: ' + (count ? JSON.stringify(count.style.display) : '-'),
        '',
        'FIRST CARD LABEL: ' +
          (first && first.querySelector('button')
            ? JSON.stringify(first.querySelector('button').getAttribute('aria-label'))
            : '-'),
        'FIRST CARD SAVED: ' + (first ? currentState(first, entries) : '-'),
        '',
        'STORAGE ENTRIES: ' + (entries ? entries.length : 'none readable'),
        entries ? entries.slice(0, 3).join('\n') : '',
      ].join('\n');
    }

    document.body.appendChild(box);
    render();
    window.setInterval(render, 1200);
  }

  function start() {
    if (started) return;
    started = true;

    injectStyles();
    observer = new MutationObserver(schedule);
    observe();
    schedule();
    watchClicks();

    // The app mounts its icons well after the theme is ready, and again once it
    // has checked which products are already on the list.
    [200, 600, 1500, 3000].forEach(function (delay) {
      window.setTimeout(schedule, delay);
    });

    window.setTimeout(debugPanel, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
